import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { useAuth, useBranding } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Lock, Palette, Clock, Save, Eye, EyeOff,
  Upload, Play, Pause, RefreshCw, Loader2, Globe,
  CheckCircle, XCircle, AlertCircle, ExternalLink,
  Image, Smartphone, Mail, Building2, Moon, Sun, MessageSquare, Shield,
  Server, Database, History, Download, HardDrive, ShieldCheck, X, QrCode, Send
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const tabs = [
  { id: 'personal', label: 'Personal Information', icon: User },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'branding', label: 'Platform Branding', icon: Palette },
  { id: 'chat-widgets', label: 'Chat Widget', icon: MessageSquare },
  { id: 'captcha', label: 'CAPTCHA', icon: Shield },
  { id: 'telegram', label: 'Telegram Alerts', icon: Send },
  { id: 'ftp-backup', label: 'FTP Backup', icon: HardDrive },
  { id: 'schedulers', label: 'Schedulers', icon: Clock },
  { id: 'resources', label: 'Resources', icon: Globe },
];

const AdminSettings = () => {
  const { user, token } = useAuth();
  const { refresh: refreshBranding } = useBranding();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);

  // Personal
  const [profile, setProfile] = useState({ full_name: '', email: '' });
  // Security
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({});

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [twoFABtnLoading, setTwoFABtnLoading] = useState(false);
  const [branding, setBranding] = useState({
    brand_name: '', logo_data: '', logo_dark_data: '', logo_light_data: '',
    favicon_data: '', mobile_logo_data: '', website_url: '', contact_email: '',
  });

  const defaultWidget = { name: 'Support Widget', enabled: false, embed_code: '' };
  const [widgets, setWidgets] = useState({
    website_widget: { ...defaultWidget },
    payment_widget: { ...defaultWidget, price: 0 },
  });
  // Schedulers
  const [schedulers, setSchedulers] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  // Resources
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  // CAPTCHA
  const [captcha, setCaptcha] = useState({
    captcha_enabled: false,
    captcha_type: 'math',
    admin_login_captcha: true,
    merchant_login_captcha: true,
    recaptcha_v2_site_key: '',
    recaptcha_v2_secret_key: '',
    hcaptcha_site_key: '',
    hcaptcha_secret_key: '',
    turnstile_site_key: '',
    turnstile_secret_key: '',
  });

  // Telegram
  const [telegram, setTelegram] = useState({ enabled: false, bot_token: '', chat_id: '' });
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);

  // FTP Backup
  const [ftp, setFtp] = useState({
    enabled: false,
    host: '', port: 21, username: '', password: '',
    remote_path: '/backups', passive_mode: true, use_ftps: false,
  });
  const [ftpLoading, setFtpLoading] = useState(false);
  const [ftpTesting, setFtpTesting] = useState(false);
  const [ftpTestResult, setFtpTestResult] = useState(null);
  const [ftpRunning, setFtpRunning] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [showFtpPassword, setShowFtpPassword] = useState(false);

  const fetchResources = async () => {
    setResourcesLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/conversion-resources`, { headers });
      setResources(data.resources || []);
    } catch { /* ignore */ }
    finally { setResourcesLoading(false); }
  };

  const fetchCaptcha = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/captcha`, { headers });
      setCaptcha(data);
    } catch { /* ignore */ }
  };

  const saveCaptcha = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/settings/captcha`, captcha, { headers });
      toast.success('CAPTCHA settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setLoading(false);
  };

  const fetchTelegram = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/telegram`, { headers });
      setTelegram({ enabled: data.enabled || false, bot_token: data.bot_token || '', chat_id: data.chat_id || '' });
    } catch { /* ignore */ }
  };

  const saveTelegram = async () => {
    setTelegramSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/telegram`, telegram, { headers });
      toast.success('Telegram settings saved');
    } catch { toast.error('Failed to save Telegram settings'); }
    finally { setTelegramSaving(false); }
  };

  const testTelegram = async () => {
    setTelegramTesting(true);
    try {
      await axios.post(`${API}/api/admin/settings/telegram/test`, {}, { headers });
      toast.success('Test message sent! Check your Telegram.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send. Check Bot Token & Chat ID.');
    } finally { setTelegramTesting(false); }
  };

  const fetchFtp = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/ftp-backup/config`, { headers });
      setFtp(data);
    } catch { /* ignore */ }
  };

  const fetchBackupHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/ftp-backup/history?limit=15`, { headers });
      setBackupHistory(data.history || []);
    } catch { /* ignore */ }
  };

  const saveFtp = async () => {
    setFtpLoading(true);
    try {
      await axios.put(`${API}/api/admin/ftp-backup/config`, ftp, { headers });
      toast.success('FTP backup settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setFtpLoading(false);
  };

  const testFtpConnection = async () => {
    setFtpTesting(true);
    setFtpTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/ftp-backup/test`, {}, { headers });
      setFtpTestResult(data);
    } catch (e) {
      setFtpTestResult({ success: false, message: e.response?.data?.detail || 'Connection failed' });
    }
    setFtpTesting(false);
  };

  const runBackupNow = async () => {
    setFtpRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/ftp-backup/run-now`, {}, { headers });
      toast.success(data.message || 'Backup started');
      setTimeout(() => { fetchBackupHistory(); setFtpRunning(false); }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start backup');
      setFtpRunning(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'personal') fetchProfile();
    if (activeTab === 'security') fetch2FAStatus();
    if (activeTab === 'branding') fetchBranding();
    if (activeTab === 'chat-widgets') fetchWidgets();
    if (activeTab === 'schedulers') fetchSchedulers();
    if (activeTab === 'resources') fetchResources();
    if (activeTab === 'captcha') fetchCaptcha();
    if (activeTab === 'telegram') fetchTelegram();
    if (activeTab === 'ftp-backup') { fetchFtp(); fetchBackupHistory(); }
  }, [activeTab]);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/profile`, { headers });
      setProfile({ full_name: data.full_name || '', email: data.email || '' });
    } catch { /* ignore */ }
  };

  const fetchBranding = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/branding`, { headers });
      setBranding({
        brand_name: data.brand_name || '',
        logo_data: data.logo_data || '',
        logo_dark_data: data.logo_dark_data || '',
        logo_light_data: data.logo_light_data || '',
        favicon_data: data.favicon_data || '',
        mobile_logo_data: data.mobile_logo_data || '',
        website_url: data.website_url || '',
        contact_email: data.contact_email || '',
      });
    } catch { /* ignore */ }
  };

  const fetchSchedulers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/schedulers`, { headers });
      setSchedulers(data);
    } catch { /* ignore */ }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/settings/profile`, profile, { headers });
      toast.success('Profile updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setLoading(false);
  };

  const changePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      return toast.error('Passwords do not match');
    }
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/settings/password`, passwords, { headers });
      toast.success('Password changed');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    setLoading(false);
  };

  const fetch2FAStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/2fa/status`, { headers });
      setTwoFAEnabled(data.totp_enabled);
    } catch { /* ignore */ }
    finally { setTwoFALoading(false); }
  };

  const handle2FASetup = async () => {
    setTwoFABtnLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/2fa/setup`, {}, { headers });
      setQrCode(data.qr_code);
      setSecretKey(data.secret);
      setVerifyCode('');
      setShowSetupDialog(true);
    } catch { toast.error('Failed to initiate 2FA setup'); }
    finally { setTwoFABtnLoading(false); }
  };

  const handle2FAEnable = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setTwoFABtnLoading(true);
    try {
      await axios.post(`${API}/api/auth/2fa/enable`, { totp_code: verifyCode }, { headers });
      setTwoFAEnabled(true);
      setShowSetupDialog(false);
      setVerifyCode('');
      toast.success('Two-factor authentication enabled!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFABtnLoading(false); }
  };

  const handle2FADisable = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setTwoFABtnLoading(true);
    try {
      await axios.post(`${API}/api/auth/2fa/disable`, { totp_code: verifyCode }, { headers });
      setTwoFAEnabled(false);
      setShowDisableDialog(false);
      setVerifyCode('');
      toast.success('Two-factor authentication disabled.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFABtnLoading(false); }
  };

  const saveBranding = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/settings/branding`, branding, { headers });
      toast.success('Branding updated');
      refreshBranding(); // Apply new branding across the app immediately
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setLoading(false);
  };

  const handleFileUpload = (field, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setBranding(prev => ({ ...prev, [field]: reader.result }));
    reader.readAsDataURL(file);
  };

  const fetchWidgets = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/chat-widgets`, { headers });
      setWidgets({
        website_widget: { name: 'Support Widget', enabled: false, embed_code: '', ...data.website_widget },
        payment_widget: { name: 'Support Widget', enabled: false, embed_code: '', price: 0, ...data.payment_widget },
      });
    } catch { /* ignore */ }
  };

  const saveWidgets = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/api/admin/settings/chat-widgets`, widgets, { headers });
      toast.success('Chat widgets updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setLoading(false);
  };

  const [schedulerRunning, setSchedulerRunning] = useState({});

  const runSchedulerNow = async (id) => {
    setSchedulerRunning(prev => ({ ...prev, [id]: true }));
    try {
      await axios.post(`${API}/api/admin/settings/schedulers/${id}/run`, {}, { headers });
      toast.success('Scheduler executed successfully');
      fetchSchedulers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run scheduler');
    } finally {
      setSchedulerRunning(prev => ({ ...prev, [id]: false }));
    }
  };

  const toggleScheduler = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/api/admin/settings/schedulers/${id}`, { status: newStatus }, { headers });
      setSchedulers(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
      toast.success(`Scheduler ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (e) { toast.error('Failed to update scheduler'); }
  };

  return (
    <AdminLayout title="Settings">
      <div className="space-y-6" data-testid="admin-settings-page">
        {/* Tabs */}
        <div className="flex gap-1 bg-card border border-gray-200 dark:border-white/10 rounded-xl p-1" data-testid="settings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Personal Information */}
        {activeTab === 'personal' && (
          <Card className="border-gray-200 dark:border-white/10" data-testid="personal-tab">
            <CardHeader>
              <CardTitle className="text-lg">Personal Information</CardTitle>
              <p className="text-sm text-muted-foreground">Update your admin account details</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Full Name</Label>
                  <Input
                    value={profile.full_name}
                    onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                    className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10"
                    data-testid="input-fullname"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Email Address</Label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10"
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={saveProfile} disabled={loading} data-testid="save-profile-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security */}
        {activeTab === 'security' && (
          <>
          {/* 2FA Dialogs */}
          <Dialog open={showSetupDialog} onOpenChange={(o) => { setShowSetupDialog(o); if (!o) setVerifyCode(''); }}>
            <DialogContent className="max-w-sm" data-testid="admin-2fa-setup-dialog">
              <DialogHeader>
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogDescription>Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {qrCode && (
                  <div className="flex justify-center">
                    <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48 rounded-lg border border-border" data-testid="admin-2fa-qr-code" />
                  </div>
                )}
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
                  <p className="text-xs font-mono font-bold text-foreground tracking-widest break-all">{secretKey}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Verification Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-lg font-mono tracking-widest"
                    data-testid="admin-2fa-verify-input"
                  />
                </div>
                <Button className="w-full" onClick={handle2FAEnable} disabled={twoFABtnLoading || verifyCode.length !== 6} data-testid="admin-2fa-enable-btn">
                  {twoFABtnLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Enable 2FA
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showDisableDialog} onOpenChange={(o) => { setShowDisableDialog(o); if (!o) setVerifyCode(''); }}>
            <DialogContent className="max-w-sm" data-testid="admin-2fa-disable-dialog">
              <DialogHeader>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogDescription>Enter your current 6-digit authenticator code to disable 2FA.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Authenticator Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-lg font-mono tracking-widest"
                    data-testid="admin-2fa-disable-input"
                  />
                </div>
                <Button variant="destructive" className="w-full" onClick={handle2FADisable} disabled={twoFABtnLoading || verifyCode.length !== 6} data-testid="admin-2fa-disable-confirm-btn">
                  {twoFABtnLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                  Disable 2FA
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="space-y-4">
          <Card className="border-gray-200 dark:border-white/10" data-testid="security-tab">
            <CardHeader>
              <CardTitle className="text-lg">Change Password</CardTitle>
              <p className="text-sm text-muted-foreground">Update your admin account password</p>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              {['current_password', 'new_password', 'confirm_password'].map((field) => (
                <div key={field}>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                    {field.replace(/_/g, ' ')}
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPw[field] ? 'text' : 'password'}
                      value={passwords[field]}
                      onChange={(e) => setPasswords({ ...passwords, [field]: e.target.value })}
                      className="bg-transparent border-gray-200 dark:border-white/10 pr-10"
                      data-testid={`input-${field}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw({ ...showPw, [field]: !showPw[field] })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPw[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <Button onClick={changePassword} disabled={loading} data-testid="change-pw-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 2FA Card */}
          <Card className="border-gray-200 dark:border-white/10" data-testid="admin-2fa-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" /> Two-Factor Authentication
              </CardTitle>
              <p className="text-sm text-muted-foreground">Protect your admin account with Google Authenticator or Authy</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  {twoFALoading ? (
                    <p className="text-sm text-muted-foreground">Checking status...</p>
                  ) : twoFAEnabled ? (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">2FA is enabled</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">2FA is not enabled</p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {twoFAEnabled ? 'Your account requires a code on every login.' : 'Enable to require a code every time you sign in.'}
                  </p>
                </div>
                {twoFALoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : twoFAEnabled ? (
                  <Button variant="outline" size="sm" className="text-xs border-red-500/20 text-red-500 hover:bg-red-500/10" onClick={() => { setVerifyCode(''); setShowDisableDialog(true); }} data-testid="admin-2fa-disable-btn">
                    Disable 2FA
                  </Button>
                ) : (
                  <Button size="sm" className="text-xs" onClick={handle2FASetup} disabled={twoFABtnLoading} data-testid="admin-2fa-setup-btn">
                    {twoFABtnLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ShieldCheck className="h-3 w-3 mr-1" />}
                    Enable 2FA
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
          </>
        )}

        {/* Platform Branding */}
        {activeTab === 'branding' && (
          <div data-testid="branding-tab" className="space-y-6">
            {/* Header */}
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" /> Platform Branding
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Customize your platform appearance. These settings will be displayed across all login pages and headers.
              </p>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* LEFT COLUMN */}
              <div className="space-y-6">
                {/* Platform Name */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Building2 className="h-4 w-4 text-primary" /> Platform Name
                  </Label>
                  <Input
                    value={branding.brand_name}
                    onChange={(e) => setBranding({ ...branding, brand_name: e.target.value })}
                    placeholder="CoinPayUp"
                    className="bg-transparent border-border"
                    data-testid="input-brandname"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">Displayed in headers and titles</p>
                </div>

                {/* Upload Logo (General / Fallback) */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Image className="h-4 w-4 text-primary" /> Upload Logo <span className="text-xs font-normal text-muted-foreground ml-1">(general fallback)</span>
                  </Label>
                  {branding.logo_data ? (
                    <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-muted/20">
                      <img src={branding.logo_data} alt="Logo" className="h-8 max-w-[100px] object-contain" />
                      <span className="text-xs text-muted-foreground flex-1">Logo uploaded</span>
                      <button className="text-xs text-destructive hover:underline" onClick={() => setBranding(b => ({...b, logo_data: ''}))}>Remove</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-full border border-dashed border-border rounded-lg py-4 cursor-pointer hover:bg-muted/30 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose File</span>
                      <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp,image/svg+xml" className="hidden"
                        onChange={(e) => handleFileUpload('logo_data', e.target.files[0])}
                        data-testid="upload-logo-input" />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">Recommended size: 200x50px (PNG, JPG, WebP, SVG)</p>
                </div>

                {/* Logo for Dark Theme */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Moon className="h-4 w-4 text-indigo-400" /> Logo for Dark Theme
                  </Label>
                  {branding.logo_dark_data ? (
                    <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-slate-900/50">
                      <img src={branding.logo_dark_data} alt="Dark Logo" className="h-8 max-w-[100px] object-contain" />
                      <span className="text-xs text-muted-foreground flex-1">Dark logo uploaded</span>
                      <button className="text-xs text-destructive hover:underline" onClick={() => setBranding(b => ({...b, logo_dark_data: ''}))}>Remove</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-full border border-dashed border-indigo-500/30 rounded-lg py-4 cursor-pointer hover:bg-indigo-500/5 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose File</span>
                      <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp,image/svg+xml" className="hidden"
                        onChange={(e) => handleFileUpload('logo_dark_data', e.target.files[0])}
                        data-testid="upload-logo-dark-input" />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">Used when the app is in dark mode</p>
                </div>

                {/* Logo for Light Theme */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Sun className="h-4 w-4 text-amber-400" /> Logo for Light Theme
                  </Label>
                  {branding.logo_light_data ? (
                    <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-amber-50/5">
                      <img src={branding.logo_light_data} alt="Light Logo" className="h-8 max-w-[100px] object-contain" />
                      <span className="text-xs text-muted-foreground flex-1">Light logo uploaded</span>
                      <button className="text-xs text-destructive hover:underline" onClick={() => setBranding(b => ({...b, logo_light_data: ''}))}>Remove</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-full border border-dashed border-amber-500/30 rounded-lg py-4 cursor-pointer hover:bg-amber-500/5 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose File</span>
                      <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp,image/svg+xml" className="hidden"
                        onChange={(e) => handleFileUpload('logo_light_data', e.target.files[0])}
                        data-testid="upload-logo-light-input" />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">Used when the app is in light mode</p>
                </div>

                {/* Upload Favicon */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Image className="h-4 w-4 text-primary" /> Upload Favicon
                  </Label>
                  {branding.favicon_data ? (
                    <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-muted/20">
                      <img src={branding.favicon_data} alt="Favicon" className="h-8 w-8 object-contain" />
                      <span className="text-xs text-muted-foreground flex-1">Favicon uploaded</span>
                      <button className="text-xs text-destructive hover:underline" onClick={() => setBranding(b => ({...b, favicon_data: ''}))}>Remove</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-full border border-dashed border-border rounded-lg py-4 cursor-pointer hover:bg-muted/30 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose File</span>
                      <input type="file" accept="image/x-icon,image/png,image/jpg" className="hidden"
                        onChange={(e) => handleFileUpload('favicon_data', e.target.files[0])}
                        data-testid="upload-favicon-input" />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">Recommended size: 32x32px (ICO, PNG, JPG)</p>
                </div>

                {/* Upload Mobile Logo */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Smartphone className="h-4 w-4 text-primary" /> Upload Mobile Logo
                  </Label>
                  {branding.mobile_logo_data ? (
                    <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-muted/20">
                      <img src={branding.mobile_logo_data} alt="Mobile Logo" className="h-8 w-8 object-contain" />
                      <span className="text-xs text-muted-foreground flex-1">Mobile logo uploaded</span>
                      <button className="text-xs text-destructive hover:underline" onClick={() => setBranding(b => ({...b, mobile_logo_data: ''}))}>Remove</button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-full border border-dashed border-border rounded-lg py-4 cursor-pointer hover:bg-muted/30 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Choose File</span>
                      <input type="file" accept="image/png" className="hidden"
                        onChange={(e) => handleFileUpload('mobile_logo_data', e.target.files[0])}
                        data-testid="upload-mobile-logo-input" />
                    </label>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">Used for Android "Add to Home Screen" icon. Recommended: 512x512px square (PNG)</p>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-6">
                {/* Website URL */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Globe className="h-4 w-4 text-primary" /> Website URL
                  </Label>
                  <Input
                    value={branding.website_url}
                    onChange={(e) => setBranding({ ...branding, website_url: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                    className="bg-transparent border-border"
                    data-testid="input-website-url"
                  />
                </div>

                {/* Contact Email */}
                <div>
                  <Label className="flex items-center gap-1.5 font-semibold mb-2">
                    <Mail className="h-4 w-4 text-primary" /> Contact Email
                  </Label>
                  <Input
                    value={branding.contact_email}
                    onChange={(e) => setBranding({ ...branding, contact_email: e.target.value })}
                    placeholder="support@yourcompany.com"
                    type="email"
                    className="bg-transparent border-border"
                    data-testid="input-contact-email"
                  />
                </div>

                {/* Logo Preview */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Logo Preview</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border border-border rounded-lg p-4 bg-slate-950 flex items-center gap-2">
                      <p className="text-[10px] text-slate-500 mb-1 absolute -mt-7">Dark</p>
                      {(branding.logo_dark_data || branding.logo_data) ? (
                        <img src={branding.logo_dark_data || branding.logo_data} alt="Dark preview" className="max-h-7 max-w-[90px] object-contain" />
                      ) : (
                        <><div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center"><Building2 className="h-3.5 w-3.5 text-primary" /></div><span className="font-semibold text-sm text-white">{branding.brand_name || 'CoinPayUp'}</span></>
                      )}
                    </div>
                    <div className="border border-border rounded-lg p-4 bg-white flex items-center gap-2">
                      {(branding.logo_light_data || branding.logo_data) ? (
                        <img src={branding.logo_light_data || branding.logo_data} alt="Light preview" className="max-h-7 max-w-[90px] object-contain" />
                      ) : (
                        <><div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center"><Building2 className="h-3.5 w-3.5 text-primary" /></div><span className="font-semibold text-sm text-gray-900">{branding.brand_name || 'CoinPayUp'}</span></>
                      )}
                    </div>
                  </div>
                  <div className="flex text-[10px] text-muted-foreground mt-1">
                    <span className="flex-1 text-center">Dark Mode</span>
                    <span className="flex-1 text-center">Light Mode</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-2">
              <Button onClick={saveBranding} disabled={loading} className="bg-primary text-white" data-testid="save-branding-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Building2 className="h-4 w-4 mr-2" />}
                Save Branding
              </Button>
            </div>
          </div>
        )}

        {/* Chat Widgets */}
        {activeTab === 'chat-widgets' && (
          <div className="space-y-6" data-testid="chat-widgets-tab">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" /> Chat Widget
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Configure support chat widgets for your website and payment pages.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Website Widget */}
              <div className="border border-border rounded-xl p-5 space-y-4 bg-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">Website Widget</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${widgets.website_widget.enabled ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                      {widgets.website_widget.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={!!widgets.website_widget.enabled}
                      onCheckedChange={v => setWidgets(w => ({ ...w, website_widget: { ...w.website_widget, enabled: v } }))}
                      data-testid="website-widget-toggle"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Displays on the CoinPayUp public website when enabled.</p>
                <div>
                  <Label className="text-xs mb-1.5 block">Name</Label>
                  <Input value={widgets.website_widget.name}
                    onChange={e => setWidgets(w => ({ ...w, website_widget: { ...w.website_widget, name: e.target.value } }))}
                    placeholder="Support Widget" className="bg-transparent border-border text-sm"
                    data-testid="website-widget-name" />
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Embed Code / Script</Label>
                  <textarea
                    value={widgets.website_widget.embed_code}
                    onChange={e => setWidgets(w => ({ ...w, website_widget: { ...w.website_widget, embed_code: e.target.value } }))}
                    placeholder={'<script src="https://..."></script>'}
                    rows={4}
                    className="w-full text-xs font-mono rounded-lg border border-border bg-transparent px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    data-testid="website-widget-embed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Paste the widget embed code or script tag here.</p>
                </div>
              </div>

              {/* Payment Widget */}
              <div className="border border-border rounded-xl p-5 space-y-4 bg-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">Payment Widget</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${widgets.payment_widget.enabled ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                      {widgets.payment_widget.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={!!widgets.payment_widget.enabled}
                      onCheckedChange={v => setWidgets(w => ({ ...w, payment_widget: { ...w.payment_widget, enabled: v } }))}
                      data-testid="payment-widget-toggle"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Displays on merchant payment/checkout pages when enabled.</p>
                <div>
                  <Label className="text-xs mb-1.5 block">Name</Label>
                  <Input value={widgets.payment_widget.name}
                    onChange={e => setWidgets(w => ({ ...w, payment_widget: { ...w.payment_widget, name: e.target.value } }))}
                    placeholder="Support Widget" className="bg-transparent border-border text-sm"
                    data-testid="payment-widget-name" />
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Price ($/month)</Label>
                  <Input type="number" min="0" step="0.01"
                    value={widgets.payment_widget.price}
                    onChange={e => setWidgets(w => ({ ...w, payment_widget: { ...w.payment_widget, price: parseFloat(e.target.value) || 0 } }))}
                    placeholder="0.00" className="bg-transparent border-border text-sm"
                    data-testid="payment-widget-price" />
                  <p className="text-xs text-muted-foreground mt-1">This price is auto-assigned to new merchant accounts on signup.</p>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Embed Code / Script</Label>
                  <textarea
                    value={widgets.payment_widget.embed_code}
                    onChange={e => setWidgets(w => ({ ...w, payment_widget: { ...w.payment_widget, embed_code: e.target.value } }))}
                    placeholder={'<script src="https://..."></script>'}
                    rows={4}
                    className="w-full text-xs font-mono rounded-lg border border-border bg-transparent px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    data-testid="payment-widget-embed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Paste the widget embed code or script tag here.</p>
                </div>
              </div>
            </div>

            <Button onClick={saveWidgets} disabled={loading} className="bg-primary text-white" data-testid="save-widgets-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
              Save Chat Widgets
            </Button>
          </div>
        )}


        {/* FTP Backup Settings */}
        {activeTab === 'ftp-backup' && (
          <div className="space-y-6" data-testid="ftp-backup-tab">
            {/* Header */}
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-primary" /> FTP Backup
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically back up your database daily at <strong>7:00 AM UTC</strong> and upload to your FTP server.
              </p>
            </div>

            {/* Enable Toggle */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Enable Automatic Backup</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Run database backup every day at 7:00 AM UTC</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${ftp.enabled ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                      {ftp.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch checked={!!ftp.enabled} onCheckedChange={v => setFtp(f => ({ ...f, enabled: v }))} data-testid="ftp-enabled-toggle" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FTP Server Settings */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Server className="h-4 w-4 text-primary/70" /> FTP Server Details
                </CardTitle>
                <p className="text-xs text-muted-foreground">Enter your FTP server credentials</p>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">FTP Host / Server Address</Label>
                    <Input value={ftp.host || ''} onChange={e => setFtp(f => ({ ...f, host: e.target.value }))}
                      placeholder="ftp.yourserver.com" className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10"
                      data-testid="ftp-host-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Port</Label>
                    <Input type="number" value={ftp.port || 21} onChange={e => setFtp(f => ({ ...f, port: parseInt(e.target.value) || 21 }))}
                      placeholder="21" className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10"
                      data-testid="ftp-port-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Username</Label>
                    <Input value={ftp.username || ''} onChange={e => setFtp(f => ({ ...f, username: e.target.value }))}
                      placeholder="ftp_user" className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10"
                      data-testid="ftp-username-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Password</Label>
                    <div className="relative mt-1.5">
                      <Input type={showFtpPassword ? 'text' : 'password'} value={ftp.password || ''}
                        onChange={e => setFtp(f => ({ ...f, password: e.target.value }))}
                        placeholder={ftp.password_set ? '(saved — enter new to change)' : 'FTP password'}
                        className="bg-transparent border-gray-200 dark:border-white/10 pr-9"
                        data-testid="ftp-password-input" />
                      <button type="button" onClick={() => setShowFtpPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showFtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Remote Backup Path</Label>
                  <Input value={ftp.remote_path || '/backups'} onChange={e => setFtp(f => ({ ...f, remote_path: e.target.value }))}
                    placeholder="/backups" className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                    data-testid="ftp-remote-path-input" />
                  <p className="text-xs text-muted-foreground mt-1">Directory on the FTP server where backup files will be stored. Created automatically if it doesn't exist.</p>
                </div>

                <div className="flex flex-wrap items-start gap-6 pt-1">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!ftp.passive_mode} onCheckedChange={v => setFtp(f => ({ ...f, passive_mode: v }))} data-testid="ftp-passive-toggle" />
                    <div>
                      <p className="text-sm font-medium">Passive Mode</p>
                      <p className="text-xs text-muted-foreground">Recommended for most firewalls</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!ftp.use_ftps} onCheckedChange={v => setFtp(f => ({ ...f, use_ftps: v }))} data-testid="ftp-ftps-toggle" />
                    <div>
                      <p className="text-sm font-medium">Use FTPS (FTP over TLS)</p>
                      <p className="text-xs text-muted-foreground">Encrypted FTP connection</p>
                    </div>
                  </div>
                </div>

                {/* Test result */}
                {ftpTestResult && (
                  <div className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
                    ftpTestResult.success
                      ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400'
                      : 'bg-destructive/5 border-destructive/20 text-destructive'
                  }`} data-testid="ftp-test-result">
                    {ftpTestResult.success
                      ? <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      : <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                    <span>{ftpTestResult.message}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-1">
                  <Button variant="outline" onClick={testFtpConnection} disabled={ftpTesting || !ftp.host}
                    className="border-gray-200 dark:border-white/10 gap-2" data-testid="ftp-test-btn">
                    {ftpTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />}
                    {ftpTesting ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button onClick={saveFtp} disabled={ftpLoading} className="bg-primary text-white gap-2" data-testid="ftp-save-btn">
                    {ftpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save Settings
                  </Button>
                  <Button variant="outline" onClick={runBackupNow} disabled={ftpRunning || !ftp.host}
                    className="gap-2 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="ftp-run-now-btn">
                    {ftpRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {ftpRunning ? 'Running...' : 'Backup Now'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Backup Info */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Schedule</p>
                    <p className="text-sm font-semibold text-foreground">Daily at 07:00 UTC</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Format</p>
                    <p className="text-sm font-semibold text-foreground">ZIP (JSON collections)</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">History Kept</p>
                    <p className="text-sm font-semibold text-foreground">Last 50 backups</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Backup History */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <History className="h-4 w-4 text-primary/70" /> Backup History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchBackupHistory} className="h-7 px-2 gap-1 text-xs">
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {backupHistory.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ftp-no-history">
                    <Database className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No backups yet. Run a backup manually or wait for the 7:00 AM schedule.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="ftp-history-list">
                    {backupHistory.map((b, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className={`h-2 w-2 rounded-full shrink-0 ${b.status === 'success' ? 'bg-emerald-400' : 'bg-destructive'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono truncate text-foreground">{b.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(b.timestamp).toLocaleString()} &bull; {b.collections_count} collections &bull; {(b.total_records || 0).toLocaleString()} records
                            {b.size_bytes > 0 && ` · ${(b.size_bytes / 1024).toFixed(1)} KB`}
                          </p>
                          {b.error && <p className="text-xs text-destructive mt-0.5">{b.error}</p>}
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                          b.status === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-destructive/10 text-destructive'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Schedulers */}
        {activeTab === 'schedulers' && (
          <div className="space-y-4" data-testid="schedulers-tab">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Scheduled Tasks</h3>
                <p className="text-sm text-muted-foreground">Manage automated background tasks and their schedules</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchSchedulers} className="border-gray-200 dark:border-white/10" data-testid="refresh-schedulers-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
            <div className="space-y-3">
              {schedulers.map((s) => (
                <Card key={s.id} className="border-gray-200 dark:border-white/10" data-testid={`scheduler-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          s.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
                        }`}>
                          <Clock className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-foreground">{s.name}</h4>
                            <Badge variant={s.status === 'active' ? 'default' : 'secondary'} className={`text-[10px] ${
                              s.status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : ''
                            }`}>
                              {s.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                          <div className="flex items-center gap-4 mt-1.5">
                            <span className="text-[11px] text-muted-foreground">Interval: <code className="font-mono text-foreground">{s.interval}</code></span>
                            {s.last_run && (
                              <span className="text-[11px] text-muted-foreground">Last run: {new Date(s.last_run).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => runSchedulerNow(s.id)}
                          disabled={schedulerRunning[s.id]}
                          className="border-gray-200 dark:border-white/10 text-blue-500 hover:bg-blue-500/10"
                          data-testid={`run-scheduler-${s.id}`}
                        >
                          {schedulerRunning[s.id]
                            ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running...</>
                            : <><Play className="h-3.5 w-3.5 mr-1.5" /> Run Now</>
                          }
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleScheduler(s.id, s.status)}
                          className={`border-gray-200 dark:border-white/10 ${
                            s.status === 'active' ? 'text-amber-500 hover:bg-amber-500/10' : 'text-emerald-500 hover:bg-emerald-500/10'
                          }`}
                          data-testid={`toggle-scheduler-${s.id}`}
                        >
                          {s.status === 'active' ? <><Pause className="h-3.5 w-3.5 mr-1.5" /> Pause</> : <><Play className="h-3.5 w-3.5 mr-1.5" /> Activate</>}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {/* CAPTCHA Settings */}
        {activeTab === 'captcha' && (
          <div className="space-y-6" data-testid="captcha-tab">
            {/* Header */}
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" /> CAPTCHA Settings
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Configure CAPTCHA protection for login forms to prevent automated attacks.
              </p>
            </div>

            {/* Enable CAPTCHA */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Enable CAPTCHA Protection</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Require CAPTCHA verification for all configured login attempts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${captcha.captcha_enabled ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                      {captcha.captcha_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={!!captcha.captcha_enabled}
                      onCheckedChange={v => setCaptcha(c => ({ ...c, captcha_enabled: v }))}
                      data-testid="captcha-enabled-toggle"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Apply On */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Apply On</CardTitle>
                <p className="text-xs text-muted-foreground">Choose which login pages require CAPTCHA verification</p>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-destructive/70" />
                    <div>
                      <p className="text-sm font-medium">Admin Login</p>
                      <p className="text-xs text-muted-foreground">Protect the admin panel login page</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!captcha.admin_login_captcha}
                    onCheckedChange={v => setCaptcha(c => ({ ...c, admin_login_captcha: v }))}
                    data-testid="admin-captcha-toggle"
                  />
                </div>
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary/70" />
                    <div>
                      <p className="text-sm font-medium">Merchant Login</p>
                      <p className="text-xs text-muted-foreground">Protect the merchant portal login page</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!captcha.merchant_login_captcha}
                    onCheckedChange={v => setCaptcha(c => ({ ...c, merchant_login_captcha: v }))}
                    data-testid="merchant-captcha-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* CAPTCHA Type */}
            <Card className="border-gray-200 dark:border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">CAPTCHA Type</CardTitle>
                <p className="text-xs text-muted-foreground">Select the CAPTCHA provider to use</p>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {[
                  { value: 'math', label: 'Math CAPTCHA', desc: 'Simple arithmetic question — no external service needed', icon: '🔢' },
                  { value: 'recaptcha_v2', label: 'Google reCAPTCHA v2', desc: 'Classic "I\'m not a robot" checkbox from Google', icon: '🔵' },
                  { value: 'hcaptcha', label: 'hCaptcha', desc: 'Privacy-focused alternative to reCAPTCHA', icon: '🟡' },
                  { value: 'turnstile', label: 'Cloudflare Turnstile', desc: 'Invisible CAPTCHA from Cloudflare with minimal friction', icon: '🟠' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setCaptcha(c => ({ ...c, captcha_type: opt.value }))}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left ${
                      captcha.captcha_type === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 dark:border-white/10 hover:border-primary/40 hover:bg-muted/30'
                    }`}
                    data-testid={`captcha-type-${opt.value}`}
                  >
                    <span className="text-lg w-6 text-center">{opt.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                    {captcha.captcha_type === opt.value && (
                      <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* API Keys for external providers */}
            {captcha.captcha_type !== 'math' && (
              <Card className="border-gray-200 dark:border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">API Keys</CardTitle>
                  <p className="text-xs text-muted-foreground">Enter your site key and secret key from the CAPTCHA provider dashboard</p>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  {captcha.captcha_type === 'recaptcha_v2' && (
                    <>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Site Key</Label>
                        <Input value={captcha.recaptcha_v2_site_key} onChange={e => setCaptcha(c => ({ ...c, recaptcha_v2_site_key: e.target.value }))}
                          placeholder="6Lc..." className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="recaptcha-site-key" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Secret Key</Label>
                        <Input type="password" value={captcha.recaptcha_v2_secret_key} onChange={e => setCaptcha(c => ({ ...c, recaptcha_v2_secret_key: e.target.value }))}
                          placeholder="6Lc..." className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="recaptcha-secret-key" />
                      </div>
                      <a href="https://www.google.com/recaptcha/admin/create" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> Get keys from Google reCAPTCHA Admin Console
                      </a>
                    </>
                  )}
                  {captcha.captcha_type === 'hcaptcha' && (
                    <>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Site Key</Label>
                        <Input value={captcha.hcaptcha_site_key} onChange={e => setCaptcha(c => ({ ...c, hcaptcha_site_key: e.target.value }))}
                          placeholder="10000000-ffff-ffff-ffff-000000000001" className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="hcaptcha-site-key" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Secret Key</Label>
                        <Input type="password" value={captcha.hcaptcha_secret_key} onChange={e => setCaptcha(c => ({ ...c, hcaptcha_secret_key: e.target.value }))}
                          placeholder="0x..." className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="hcaptcha-secret-key" />
                      </div>
                      <a href="https://dashboard.hcaptcha.com/sites/new" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> Get keys from hCaptcha Dashboard
                      </a>
                    </>
                  )}
                  {captcha.captcha_type === 'turnstile' && (
                    <>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Site Key</Label>
                        <Input value={captcha.turnstile_site_key} onChange={e => setCaptcha(c => ({ ...c, turnstile_site_key: e.target.value }))}
                          placeholder="0x4AAAAAAA..." className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="turnstile-site-key" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Secret Key</Label>
                        <Input type="password" value={captcha.turnstile_secret_key} onChange={e => setCaptcha(c => ({ ...c, turnstile_secret_key: e.target.value }))}
                          placeholder="0x4AAAAAAA..." className="mt-1.5 bg-transparent border-gray-200 dark:border-white/10 font-mono text-sm"
                          data-testid="turnstile-secret-key" />
                      </div>
                      <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> Get keys from Cloudflare Turnstile Dashboard
                      </a>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            <Button onClick={saveCaptcha} disabled={loading} className="bg-primary text-white" data-testid="save-captcha-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
              Save CAPTCHA Settings
            </Button>
          </div>
        )}

        {activeTab === 'telegram' && (
          <div className="space-y-6" data-testid="telegram-tab">
            <div>
              <h3 className="text-lg font-semibold">Telegram Notifications</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Receive alerts for node failures, network outages, new tickets and SLA breaches via Telegram bot.
              </p>
            </div>

            <Card className="border-border">
              <CardContent className="pt-6 space-y-5">
                {/* Enable / Disable toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                  <div>
                    <p className="font-medium text-sm">Telegram Alerts</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {telegram.enabled ? 'Notifications are active' : 'Notifications are disabled'}
                    </p>
                  </div>
                  <Switch
                    checked={telegram.enabled}
                    onCheckedChange={v => setTelegram(t => ({ ...t, enabled: v }))}
                    data-testid="telegram-enabled-toggle"
                  />
                </div>

                {/* Bot Token */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Bot Token</Label>
                  <Input
                    type="password"
                    value={telegram.bot_token}
                    onChange={e => setTelegram(t => ({ ...t, bot_token: e.target.value }))}
                    placeholder="123456789:AAF..."
                    className="font-mono text-sm"
                    data-testid="telegram-bot-token"
                  />
                  <p className="text-xs text-muted-foreground">
                    Get from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">@BotFather</a> on Telegram
                  </p>
                </div>

                {/* Chat ID */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Chat ID</Label>
                  <Input
                    value={telegram.chat_id}
                    onChange={e => setTelegram(t => ({ ...t, chat_id: e.target.value }))}
                    placeholder="-1001234567890 or your user ID"
                    className="font-mono text-sm"
                    data-testid="telegram-chat-id"
                  />
                  <p className="text-xs text-muted-foreground">
                    Send a message to your bot, then visit{' '}
                    <code className="bg-muted px-1 rounded text-xs">
                      https://api.telegram.org/bot&#123;TOKEN&#125;/getUpdates
                    </code>{' '}
                    to find your Chat ID.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center gap-3">
              <Button
                onClick={saveTelegram}
                disabled={telegramSaving}
                className="bg-primary text-white"
                data-testid="save-telegram-btn"
              >
                {telegramSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
              <Button
                variant="outline"
                onClick={testTelegram}
                disabled={telegramTesting || !telegram.enabled || !telegram.bot_token || !telegram.chat_id}
                className="border-border"
                data-testid="test-telegram-btn"
              >
                {telegramTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Test Message
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="space-y-4" data-testid="resources-tab">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Currency Conversion Resources</h3>
                <p className="text-sm text-muted-foreground">Free APIs used for fiat → crypto conversion, tried in priority order</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchResources} disabled={resourcesLoading}
                className="border-gray-200 dark:border-white/10" data-testid="refresh-resources-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${resourcesLoading ? 'animate-spin' : ''}`} /> Check Status
              </Button>
            </div>
            <div className="space-y-3">
              {resourcesLoading && !resources.length ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : resources.map((r, i) => (
                <Card key={r.name} className="border-gray-200 dark:border-white/10" data-testid={`resource-${r.name}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-muted-foreground">#{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-foreground">{r.name}</h4>
                          {r.status === 'ok' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                              <CheckCircle className="h-2.5 w-2.5" /> Online
                            </span>
                          ) : r.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/10 text-red-500 border border-red-500/20">
                              <XCircle className="h-2.5 w-2.5" /> Offline
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">
                              <AlertCircle className="h-2.5 w-2.5" /> No data
                            </span>
                          )}
                          {!r.key_required && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20">
                              No API Key
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.description}</p>
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          {r.rate_limit && (
                            <span className="text-[11px] text-muted-foreground">Rate limit: <code className="font-mono text-foreground">{r.rate_limit}</code></span>
                          )}
                          {(r.price_eth_usd ?? r.price_usdt_usd) != null && (
                            <span className="text-[11px] text-muted-foreground">ETH/USD: <code className="font-mono text-foreground">${Number(r.price_eth_usd ?? r.price_usdt_usd).toFixed(2)}</code></span>
                          )}
                          {r.error && (
                            <span className="text-[11px] text-red-400 font-mono truncate max-w-xs">{r.error}</span>
                          )}
                        </div>
                        {r.url && (
                          <a href={r.docs || r.url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline mt-1">
                            <ExternalLink className="h-2.5 w-2.5" /> View Documentation
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
